import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Sparkles } from "lucide-react";
import api from "@/lib/api";

const PROMPTS = [
  "What are you most curious about right now?",
  "Where does desire show up in your body?",
  "Name a fantasy that feels genuinely yours.",
  "What would confidence feel like today?",
  "What pleasure would you like to know better?",
  "Where do you feel most seen — and where do you want to be?",
];

export default function Journal() {
  const [entries, setEntries] = useState([]);
  const [prompt, setPrompt] = useState(PROMPTS[0]);
  const [body, setBody] = useState("");
  const [reflection, setReflection] = useState("");
  const [reflecting, setReflecting] = useState(false);

  const load = () => api.get("/journal").then(r => setEntries(r.data));
  useEffect(() => { load(); }, []);

  const save = async (e) => {
    e.preventDefault();
    if (!body.trim()) return;
    try {
      await api.post("/journal", { body, prompt });
      toast.success("Entry saved.");
      setBody(""); setReflection(""); load();
    } catch { toast.error("Save failed."); }
  };

  const reflect = async () => {
    if (!body.trim()) return toast.error("Write something first.");
    setReflecting(true);
    try {
      const { data } = await api.post("/journal/reflect", { entry: body });
      setReflection(data.reflection);
    } catch { toast.error("Reflection unavailable."); } finally { setReflecting(false); }
  };

  const shuffle = () => setPrompt(PROMPTS[Math.floor(Math.random() * PROMPTS.length)]);

  return (
    <div data-testid="journal-page" className="max-w-4xl mx-auto px-6 sm:px-10 py-16">
      <div className="eyebrow-lg text-center">Reflection</div>
      <h1 className="mt-8 h-editorial text-5xl text-center">Write it <span className="italic-serif text-[#D4AF37]">honest.</span></h1>
      <p className="mt-4 text-center text-sm text-zinc-400 max-w-xl mx-auto">Notes and reflections from before and after — held privately, held by you.</p>

      <div className="mt-10 hairline p-8">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div className="eyebrow eyebrow-blue">Prompt</div>
          <button data-testid="prompt-shuffle" onClick={shuffle} className="text-xs text-[#00D1FF] underline-editorial">Shuffle prompt</button>
        </div>
        <div className="mt-3 font-serif-editorial text-2xl italic-serif text-zinc-100">{prompt}</div>
        <form onSubmit={save} className="mt-6 space-y-4">
          <textarea data-testid="journal-body" rows={8} value={body} onChange={e => setBody(e.target.value)} className="w-full px-4 py-3" placeholder="Whatever needs to leave your body first." />
          <div className="flex flex-wrap gap-3">
            <button data-testid="journal-save" type="submit" className="rc-btn-primary">Save Entry</button>
            <button data-testid="journal-reflect" type="button" onClick={reflect} disabled={reflecting} className="rc-btn-gold inline-flex items-center gap-2 disabled:opacity-50">
              <Sparkles className="w-3.5 h-3.5" /> {reflecting ? "Reflecting…" : "Reflect with AI"}
            </button>
          </div>
        </form>
        {reflection && (
          <div data-testid="reflection-block" className="mt-8 p-6 hairline border-[#D4AF37]/30 bg-[#D4AF37]/5">
            <div className="eyebrow">Reflection</div>
            <p className="mt-3 text-zinc-200 italic-serif text-lg leading-relaxed">{reflection}</p>
          </div>
        )}
      </div>

      <div className="mt-16">
        <div className="eyebrow eyebrow-blue">Past Entries</div>
        <h2 className="mt-3 h-editorial text-3xl">The archive.</h2>
        {entries.length === 0 ? (
          <div className="mt-8 text-zinc-500 text-sm">Nothing here yet — the first page is always blank.</div>
        ) : (
          <div className="mt-8 space-y-3">
            {entries.map(e => (
              <div key={e.entry_id} data-testid={`entry-${e.entry_id}`} className="hairline p-6">
                <div className="eyebrow">{new Date(e.created_at).toLocaleString()}</div>
                {e.prompt && <div className="italic-serif text-[#D4AF37] mt-2">{e.prompt}</div>}
                <p className="mt-3 text-sm text-zinc-300 whitespace-pre-wrap">{e.body}</p>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
